import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { ADMIN } from '@/constants/testIds';
import { LayoutDashboard, Package, ShoppingBag, Calendar, Users, Stethoscope, BookOpen, Star, HelpCircle, Sparkles, FlaskConical, Ticket, Settings, LogOut, Menu, X, ScrollText, BarChart3, PenTool, ClipboardList, Utensils } from 'lucide-react';

const NAV = [
  { to: '/admin', end: true, label: 'Overview', icon: LayoutDashboard, id: ADMIN.navHome },
  { to: '/admin/analytics', label: 'Analytics', icon: BarChart3, id: 'admin-nav-analytics' },
  { to: '/admin/ai-writer', label: 'AI Content Writer', icon: PenTool, id: 'admin-nav-ai-writer' },
  { to: '/admin/products', label: 'Products', icon: Package, id: ADMIN.navProducts },
  { to: '/admin/orders', label: 'Orders', icon: ShoppingBag, id: ADMIN.navOrders },
  { to: '/admin/appointments', label: 'Appointments', icon: Calendar, id: ADMIN.navAppointments },
  { to: '/admin/diet-plans', label: 'Diet Planning', icon: Utensils, id: 'admin-nav-diet-plans' },
  { to: '/admin/assessments', label: 'Prakriti Reports', icon: ClipboardList, id: 'admin-nav-assessments' },
  { to: '/admin/users', label: 'Users', icon: Users, id: ADMIN.navUsers },
  { to: '/admin/doctors', label: 'Doctors', icon: Stethoscope, id: ADMIN.navDoctors },
  { to: '/admin/articles', label: 'Knowledge Hub', icon: BookOpen, id: ADMIN.navArticles },
  { to: '/admin/verses', label: 'Ayurvedic Verses', icon: ScrollText, id: 'admin-nav-verses' },
  { to: '/admin/testimonials', label: 'Testimonials', icon: Star, id: ADMIN.navTestimonials },
  { to: '/admin/faqs', label: 'FAQs', icon: HelpCircle, id: ADMIN.navFaqs },
  { to: '/admin/concerns', label: 'Health Concerns', icon: Sparkles, id: ADMIN.navConcerns },
  { to: '/admin/evidence', label: 'Clinical Evidence', icon: FlaskConical, id: ADMIN.navEvidence },
  { to: '/admin/coupons', label: 'Coupons', icon: Ticket, id: ADMIN.navCoupons },
  { to: '/admin/settings', label: 'Site Settings', icon: Settings, id: ADMIN.navSettings },
];

const AdminShell = () => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <div className="min-h-screen bg-sans_bone">
      {/* Sidebar */}
      <aside className={`fixed top-0 bottom-0 left-0 w-64 bg-white border-r border-sans_line z-30 transform ${open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'} transition-transform`}>
        <div className="p-6 border-b border-sans_line">
          <Link to="/" className="font-serif text-xl text-sans_ink">Saans Ayurved</Link>
          <div className="text-[10px] uppercase tracking-[0.22em] text-sans_mute mt-1">Admin console</div>
        </div>
        <nav className="p-3 space-y-1 overflow-y-auto max-h-[calc(100vh-160px)]">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={n.id}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 text-[13px] transition-colors ${
                  isActive ? 'bg-sans_ink text-sans_bone' : 'text-sans_ink hover:bg-sans_bone'
                }`}
              >
                <Icon className="w-4 h-4" strokeWidth={1.5} />
                {n.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-sans_line bg-white">
          <div className="text-xs text-sans_mute mb-2">{user?.email}</div>
          <button onClick={async () => { await logout(); window.location.href = '/'; }} className="flex items-center gap-2 text-xs text-sans_ink hover:opacity-70">
            <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} /> Sign out
          </button>
        </div>
      </aside>

      {/* Topbar (mobile only) */}
      <div className="lg:hidden h-14 bg-white border-b border-sans_line flex items-center justify-between px-4 sticky top-0 z-20">
        <button onClick={() => setOpen((s) => !s)} className="text-sans_ink" aria-label="Menu">
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
        <div className="font-serif text-lg text-sans_ink">Admin</div>
        <div className="w-5" />
      </div>

      {/* Content */}
      <main className="lg:pl-64">
        <div className="p-6 md:p-12 max-w-7xl">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminShell;
